import { Injectable, signal, computed, inject, DestroyRef, PLATFORM_ID, Signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { fromEvent, merge, Subject, Observable } from 'rxjs';
import { debounceTime, map, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoggerService } from '@core/services/logger.service';

/**
 * User Activity Service - Track user engagement and idle detection
 *
 * Features:
 * - Monitors mouse, keyboard, scroll, and touch events
 * - Debounced activity detection to avoid excessive updates
 * - Signal-based reactive state (Angular 17+)
 * - Configurable activity timeout
 * - Observable stream for RxJS integration
 * - SSR-safe with platform detection
 * - Automatic cleanup on destroy
 *
 * Usage:
 * ```typescript
 * constructor(private activityService: UserActivityService) {}
 *
 * ngOnInit() {
 *   // Start monitoring
 *   this.activityService.startMonitoring();
 *
 *   // Configure timeout (optional)
 *   this.activityService.activityTimeoutMs.set(15 * 60 * 1000); // 15 minutes
 *
 *   // React to idle state
 *   effect(() => {
 *     if (!this.activityService.isActive()) {
 *       this.logger.debug('User is idle');
 *     }
 *   });
 *
 *   // Or use observable
 *   this.activityService.activity$.subscribe(time => {
 *     this.logger.debug('Activity detected', { time });
 *   });
 * }
 * ```
 *
 * Integration with Auto-logout (DEV-406):
 * - Session management service subscribes to isActive signal
 * - Triggers token refresh before timeout
 * - Shows warning modal when approaching timeout
 * - Auto-logout when isActive becomes false
 */
@Injectable({
  providedIn: 'root'
})
export class UserActivityService {
  // Dependencies
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly logger = inject(LoggerService).withContext({ component: 'UserActivityService' });

  // Configuration
  /** Activity timeout in milliseconds (default: 30 minutes) */
  readonly activityTimeoutMs = signal(30 * 60 * 1000);

  /** Debounce time for activity events in milliseconds (default: 500ms) */
  readonly debounceMs = signal(500);

  // State
  private readonly lastActivityTimeSignal = signal(new Date());
  private isMonitoring = signal(false);

  // Subject for activity events (internal)
  private readonly activitySubject = new Subject<Date>();

  // Public signals
  /** Last detected activity timestamp */
  readonly lastActivityTime: Signal<Date> = this.lastActivityTimeSignal.asReadonly();

  /** Computed signal indicating if user is currently active (within timeout window) */
  readonly isActive: Signal<boolean> = computed(() => {
    const lastActivity = this.lastActivityTimeSignal();
    const timeout = this.activityTimeoutMs();
    const elapsed = Date.now() - lastActivity.getTime();
    return elapsed < timeout;
  });

  /** Observable stream of activity events (for RxJS-based services) */
  readonly activity$: Observable<Date> = this.activitySubject.asObservable();

  /**
   * Start monitoring user activity
   * Safe to call multiple times (idempotent)
   */
  startMonitoring(): void {
    if (this.isMonitoring()) {
      this.logger.debug('Activity monitoring already started');
      return;
    }

    if (!isPlatformBrowser(this.platformId)) {
      this.logger.warn('Cannot start activity monitoring: not in browser environment');
      return;
    }

    this.logger.info('Starting activity monitoring', {
      timeout: this.activityTimeoutMs(),
      debounce: this.debounceMs()
    });

    this.setupEventListeners();
    this.isMonitoring.set(true);

    // Initialize with current time
    this.resetActivity();
  }

  /**
   * Stop monitoring user activity
   * Cleanup is automatic via takeUntilDestroyed
   */
  stopMonitoring(): void {
    if (!this.isMonitoring()) {
      this.logger.debug('Activity monitoring not running');
      return;
    }

    this.logger.info('Stopping activity monitoring');
    this.isMonitoring.set(false);

    // Note: Event listeners are automatically cleaned up by takeUntilDestroyed
  }

  /**
   * Manually reset activity timestamp
   * Useful for programmatic activity simulation
   */
  resetActivity(): void {
    const now = new Date();
    this.lastActivityTimeSignal.set(now);
    this.activitySubject.next(now);

    this.logger.debug('Activity reset', { timestamp: now.toISOString() });
  }

  /**
   * Get time remaining until timeout (in milliseconds)
   * Returns 0 if already timed out
   */
  getTimeUntilTimeout(): number {
    const lastActivity = this.lastActivityTimeSignal();
    const timeout = this.activityTimeoutMs();
    const elapsed = Date.now() - lastActivity.getTime();
    const remaining = timeout - elapsed;
    return Math.max(0, remaining);
  }

  /**
   * Get time remaining until timeout as a readable string
   * @returns String like "5m 30s" or "Idle"
   */
  getTimeUntilTimeoutFormatted(): string {
    const remaining = this.getTimeUntilTimeout();

    if (remaining === 0) {
      return 'Idle';
    }

    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }

    return `${seconds}s`;
  }

  /**
   * Setup event listeners for activity detection
   * Listens to: mousemove, keydown, scroll, touchstart
   */
  private setupEventListeners(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const debounce = this.debounceMs();

    // Mouse activity
    const mouseMove$ = fromEvent(document, 'mousemove');

    // Keyboard activity
    const keyDown$ = fromEvent(document, 'keydown');

    // Scroll activity
    const scroll$ = fromEvent(document, 'scroll', { passive: true, capture: true });

    // Touch activity (mobile support)
    const touchStart$ = fromEvent(document, 'touchstart', { passive: true });

    // Merge all activity sources
    merge(mouseMove$, keyDown$, scroll$, touchStart$)
      .pipe(
        debounceTime(debounce),
        map(() => new Date()),
        tap(timestamp => {
          this.lastActivityTimeSignal.set(timestamp);
          this.activitySubject.next(timestamp);
        }),
        tap(timestamp => {
          this.logger.debug('Activity detected', {
            timestamp: timestamp.toISOString(),
            remaining: this.getTimeUntilTimeoutFormatted()
          });
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(); // No error handler needed: DOM event observables (merge of mousemove/keydown/scroll/touch) cannot error

    this.logger.debug('Event listeners attached', {
      events: ['mousemove', 'keydown', 'scroll', 'touchstart'],
      debounce
    });
  }
}
